<template lang="pug">
  .five-for-five-drawer
    template(v-if="!isFunneled && !overlay && additionalAppPageFilter")
      RogueButton.five-for-five-button(type="button" color="black" @click="uiStore.showOverlay('five-for-five', rogue)" :stop="false" data-track="mbn-5for5-drawer-button")
        //- Only one of these shows per breakpoint; together with "Exclusions Apply" they name the button
        StaticImage.img(:src="fiveForFiveWhiteImgSrc" :width="90" :height="40" :alt="dealText")
        StaticImage.mobile-img(:src="fiveForFiveMobileBadgeSrc" :width="310" :height="28" :alt="dealText")
        p {{__('Exclusions Apply')}}
      button.a11y.five-for-five-close(type="button" :aria-label="__('Close {deal} badge', { deal: dealText })" data-track="mbn-5for5-drawer-close" @click="uiStore.hideFiveForFiveDrawer = true")
        Icon(name="x")
    Drawer(v-if="overlay === 'five-for-five' || $rogue.ssrContext?.queryParams?.SAVE_TRANSLATIONS" :title="`5 for ${rogue.currency(5, { hideFractionalUnits: true })} FAQ`" :disableScroll="false" @hide="uiStore.hideOverlay()")
      .black-header
        //- Decorative: the drawer title already says it
        StaticImage.img(:src="fiveForFiveWhiteImgSrc" :width="100" :height="50" alt="")
      .drawer-content
        h4 {{__(`What items qualify for the 5 for ${rogue.currency(5, { hideFractionalUnits: true })} shipping deal?`)}}
        p {{__(`Qualifying items will be marked with this 5/${rogue.currency(5, { hideFractionalUnits: true })} icon:`)}}
        rogue-link(to="/deals?deal_type=5for5")
          StaticImage.img(:src="fiveForFiveBlackImgSrc" :width="176" :height="84" :alt="__('{deal} icon', { deal: dealText })")
        rogue-link.products(to="/deals?deal_type=5for5") {{ __('Shop for qualifying products') }}
        h4(v-if="rogue.site.code === 'USA'") {{__('Can you ship outside the cont. 48?')}}
        p(v-if="rogue.site.code === 'USA'") {{__('This promotion is only for the contiguous US 48 only and does not apply to Alaska, Hawaii, Canada, or any other International location.')}}
        h4(v-if="['EUR', 'GBR'].includes(rogue.site.code)") {{__('Can you ship outside the Gray Zone?')}}
        p(v-if="['EUR', 'GBR'].includes(rogue.site.code)") {{__('This promotion is only for the Gray Zone only and does not apply to areas outside of the Gray Zone.')}}
        h4 {{__(`How many items can I use the 5 for ${rogue.currency(5, { hideFractionalUnits: true })} promotion?`)}}
        p {{__(`The promotion can be used twice per customer. It will no longer be valid after placing two successful 5 for ${rogue.currency(5, { hideFractionalUnits: true })} orders.`)}}
        h4 {{__('What if an item\'s status changes after an order has been placed?')}}
        p {{__(`If an item is marked in-stock and eligible for 5 for ${rogue.currency(5, { hideFractionalUnits: true })} at the time of checkout, the deal still applies, assuming all other terms are met, even if the item becomes out-of-stock or on back order. However, we can only ship promotional orders as complete orders.`)}}
        h4 {{__('Are Closeout items excluded from this sale?')}}
        p {{__('Yes. Closeout items are excluded from this sale and do not count toward the 5 items.')}}
        h4 {{__('Why are certain vendors or items excluded?')}}
        p {{__(`The 5 for ${rogue.currency(5, { hideFractionalUnits: true })} deal applies to specific in-stock items in the Rogue warehouse, and not closeout items or products shipped from outside vendors / facilities. If a product doesn\'t include the 5 or ${rogue.currency(5, { hideFractionalUnits: true })} symbol, it does not qualify for the deal.`)}}
        h4(v-if="rogue.site.code === 'USA'") {{__(`Does 5 for ${rogue.currency(5, { hideFractionalUnits: true })} apply to Alaska or Hawaii?`)}}
        p(v-if="rogue.site.code === 'USA'") {{__(`Unfortunately 5 for ${rogue.currency(5, { hideFractionalUnits: true })} does not apply to Alaska or Hawaii.`)}}
        h4(v-if="['EUR', 'GBR'].includes(rogue.site.code)") {{__('What is the Gray Zone and how do I view it?')}}
        p(v-if="['EUR', 'GBR'].includes(rogue.site.code)" v-html="__('All Rogue Europe shipping promotions are extended to the Gray Zone only and this map can be viewed  <a href=\"{shippingPromotionsUrl}\">here</a>.', {'shippingPromotionsUrl': `${rogue.l10n.path}/shipping-promotions`})")
        h4 {{__('Do gift certificates qualify as an item?')}}
        p {{__('No. Gift certificates do not count toward the 5 items.')}}
        br
        p {{__('For questions and comments, ')}}
          rogue-link.contact(to="/contact-us") {{ __('contact us.') }}
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'
import { useUIStore } from '@/js/stores/uiStore'

const route = useRoute()
const rogue = useRogue()
const uiStore = useUIStore()

const overlay = computed<string | null>(() => uiStore.overlay)

const isFunneled = computed<boolean>(() => !!route.meta?.isFunneled)

const additionalAppPageFilter = computed<boolean>(() => !rogue.appContext
  || (rogue.appContext && (['product', 'category'].includes(route.meta?.type)
  || route.path.indexOf('/deals') === 0)))

// The text in the 5-for-5 artwork, in the site's currency
const dealText = computed(() => rogue.__('5 items ship for {price}', { price: rogue.currency(5, { hideFractionalUnits: true }) }))

const fiveForFiveWhiteImgSrc = computed(() => {
  switch (rogue.site.code) {
    case 'EUR':
      return '/UI/matte_black/5-for-5-EU-White.svg'
    case 'GBR':
      return '/UI/matte_black/5-for-5-Pound-White.svg'
    default:
      return '/UI/matte_black/5-for-5-US-White.svg'
  }
})

const fiveForFiveBlackImgSrc = computed(() => {
  switch (rogue.site.code) {
    case 'EUR':
      return '/UI/matte_black/5-for-5-EU-Black.svg'
    case 'GBR':
      return '/UI/matte_black/5-for-5-Pound-Black.svg'
    default:
      return '/UI/matte_black/5-for-5-US-Black.svg'
  }
})

const fiveForFiveMobileBadgeSrc = computed(() => {
  switch (rogue.site.code) {
    case 'EUR':
      return '/UI/matte_black/5for5-badge-Sticky-Banner-Euro_cxyfno.svg'
    case 'GBR':
      return '/UI/matte_black/5for5-Sticky-Banner-Pound_tshpgu.svg'
    default:
      return '/UI/matte_black/5for5-badge_dbbqjp.svg'
  }
})
</script>
<style lang="scss" scoped>

.five-for-five-drawer .black-header {
  background: #000;
}

.five-for-five-drawer .black-header {
  text-align: center;
  position: relative;
  height: 150px;
}

.five-for-five-drawer .black-header .img {
  max-width: 200px;
  margin-left: auto;
  margin-right: auto;
  top: 24px;
}

.five-for-five-drawer .img-row {
  display: flex;
  flex-direction: row;
}

.five-for-five-drawer .drawer-content {
  margin: $size-s;
}

.five-for-five-drawer .drawer-content a.products {
  color: $color-gray-x-dark;
  text-align: center;
  margin-bottom: 20px;
  display: block;
}

.five-for-five-drawer .drawer-content a.contact {
  color: $color-gray-x-dark;
}

.five-for-five-drawer .drawer-content .img {
  max-width: 150px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 16px;
}

.five-for-five-drawer Button.five-for-five-button {
  position: fixed !important;
  width: 100%;
  height: 64px;
  right: 0;
  bottom: 0;
  margin-bottom: 0 !important;
  z-index: 14;
  border-radius: 0;
  background: black !important;
}

.five-for-five-drawer Button.five-for-five-button .img {
  display: none;
}

.five-for-five-drawer Button.five-for-five-button .mobile-img {
  max-width: 200px;
  margin: 0 auto;
  display: block;
}

.five-for-five-drawer Button.five-for-five-button p {
  margin: 5px 0 0 0;
  font-size: $size-xxs;
  text-decoration: underline;
}

.five-for-five-drawer .five-for-five-close {
  position: fixed;
  right: $space-xxs;
  bottom: calc(64px - #{$size-xl} - #{$space-xxs});
  z-index: 14;
  display: flex;
  align-items: center;
  justify-content: center;
  width: $size-xl;
  height: $size-xl;
  color: $color-white;
  background: $color-black;
  border: $border-width-thin solid $color-white;
  border-radius: $radius-circle;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $color-black;
    background: $color-white;
    border-color: $color-black;
  }

  :deep(.icon) {
    width: $size-xxs;
    height: $size-xxs;
  }
}

@include not-mobile() {
  .five-for-five-drawer Button.five-for-five-button {
    position: fixed !important;
    width: 150px;
    height: 100px;
    right: 0;
    bottom: 100px;
    z-index: 15;
    border-radius: $size-xxs !important;
    background: black !important;
  }
  .five-for-five-drawer Button.five-for-five-button .img {
    top: -3px;
    display: block;
  }
  .five-for-five-drawer Button.five-for-five-button .mobile-img {
    display: none;
  }
  .five-for-five-drawer Button.five-for-five-button p {
    margin-top: 5px;
    margin-left: 10px;
  }
  .five-for-five-drawer .five-for-five-close {
    bottom: calc(200px - #{$size-xl} / 2);
    z-index: 15;
  }
}

</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .five-for-five-drawer Button.five-for-five-button p {
    text-transform: capitalize;
  }
}
</style>
